import {
    type FC,
    type MouseEvent,
    type ReactNode,
    useContext,
} from "react";
import FontAwesomeLight from "../../../Utilities/Platform/FontAwesome/FontAwesomeLight";
import Button from "../../Buttons/Button";
import SaveButton from "../../Buttons/SaveButton";
import { DragDropComponentSettings } from "./DragDropComponentSettings";
import { DragDropNewComponent } from "./DragDropNewComponent";
import { DragDropEditorContext } from "./DragDropTypes";

export type DragDropTabMenu = "components" | "settings";

interface Props {
    selected: null | string;
    preview: boolean;
    onPreviewClick: (e: MouseEvent) => void;
    onCancelClick: (e: MouseEvent<HTMLButtonElement>) => void;
    onSaveClick: (e: MouseEvent) => void;
    onHighlight: (type: string | null) => void;
    onTabChange: (tab: DragDropTabMenu) => void;
    tab: DragDropTabMenu;
}

export const DragDropMenu: FC<Props> = ({
    selected,
    preview,
    onPreviewClick,
    onCancelClick,
    onSaveClick,
    onHighlight,
    onTabChange,
    tab,
}) => {
    const { items } = useContext(DragDropEditorContext);

    let page: ReactNode;

    if(tab === "settings" && selected) {
        page = (
            <DragDropComponentSettings
                id={selected}
            />
        );
    } else {
        page = (
            <div className="grid grid-cols-[repeat(auto-fit,minmax(6rem,1fr))] w-full p-4 gap-4">
                {Object.keys(items).map(type => (
                    <div
                        key={type}
                        className="flex justify-center items-center"
                    >
                        <DragDropNewComponent
                            type={type}
                            onHighlight={onHighlight}
                        />
                    </div>
                ))}
            </div>
        );
    }

    return (
        <div className="flex flex-col w-[200px] lg:w-[300px] xl:w-[400px] bg-white dark:bg-dark-background">
            <div className="grid grid-cols-2 w-full">
                <button
                    type="button"
                    className={
                        "py-4 text-lg border-b-4 cursor-pointer disabled:cursor-not-allowed enabled:bg-neutral-200 " +
                        "dark:enabled:bg-dark-900 dark:enabled:text-white " + (
                            tab === "components" ?
                                "border-b-blue-500" :
                                "border-b-gray-400 enabled:hover:bg-neutral-300 dark:enabled:hover:bg-dark-700"
                        )
                    }
                    onClick={() => {
                        onTabChange("components");
                    }}
                >
                    <FontAwesomeLight icon="box-full" /><span className="ml-2 hidden xl:inline">Components</span>
                </button>
                <button
                    type="button"
                    className={
                        "py-4 text-lg border-b-4 cursor-pointer enabled:bg-neutral-200 " +
                        "dark:enabled:bg-dark-900 dark:enabled:text-white disabled:cursor-not-allowed " +
                        "disabled:bg-neutral-300 disabled:text-neutral-500 dark:disabled:bg-neutral-700 " +
                        "dark:disabled:text-neutral-400 " + (
                            tab === "settings" ?
                                "border-b-blue-500" :
                                "border-b-gray-400 enabled:hover:bg-neutral-300 dark:enabled:hover:bg-dark-700"
                        )
                    }
                    disabled={!selected}
                    onClick={() => {
                        if(!selected) {
                            return;
                        }

                        onTabChange("settings");
                    }}
                >
                    <FontAwesomeLight icon="cog" /><span className="ml-2 hidden xl:inline">Settings</span>
                </button>
            </div>
            <div className="grow h-full overflow-auto">
                {page}
            </div>
            <div
                className={
                    "flex flex-col xl:flex-row justify-between p-4 text-lg border-t dark:border-t-0 dark:bg-dark-900"
                }
            >
                <Button
                    type="button"
                    classes={[ "button-cancel mb-2 xl:mb-0" ]}
                    onClick={onPreviewClick}
                >
                    {preview ? "Edit" : "Preview"}
                </Button>
                <div className="contents xl:block">
                    <Button
                        type="button"
                        classes={[ "button-cancel mb-2 xl:mb-0 xl:mr-4" ]}
                        onClick={onCancelClick}
                    >
                        Cancel
                    </Button>
                    <SaveButton
                        type="button"
                        onClick={onSaveClick}
                    />
                </div>
            </div>
        </div>
    );
};
